// ═══════════════════════════════════════════════════════════════════
// willieModel3D.jsx — WILLIE, SCULPTED   (caP'n, 2026-09-16, THE PLAZA UPDATE #1)
// ═══════════════════════════════════════════════════════════════════
// V'ael's Champion and groundskeeper, off his front art: the mossy turtle
// head, the huge sad brown eyes, the torn leather vest over the grey
// shirt, the buckle, the two pouches (the teal one's W glows), the patched
// shorts, the bare feet — and THE BOOK, open in his left hand. He reads as
// he walks; now and then, standing still, he looks up and points at you
// (the art's own pose). 3D only; 2D screens keep their pictures.
// `public/models/willie.glb`, built in the Cowork sandbox (builder in
// _scratch/willie3d). Same contract as laostreModel3D.jsx.
// The painted WillyBody stays in plazaFolk3D.jsx as the fail-safe.
// ⭐ 2026-09-23 — THE BLENDER PASS (the PLAZA's Willie only). willie.glb is now
// built by gamemaster/models/sculpt/builders/willie_blender: a new head off
// caP'n's three face pictures (heavy tired lids over near-black-brown irises,
// bag rolls, snout pad + teardrop nostrils, a downturned mouth, leaf moss on a
// crown mat, grey pebbles) on v1's SAME skeleton, bone names and READ_POSE, so
// williePose drives it unchanged. New part name: wil_pebbles (rides the head,
// takes the ink). Its crown measures 1.34 (v1's spiky moss reached 1.40); MODEL_TOP
// stays 1.4 so the BODY keeps its v1 size. The Forge keeps willie_forge.glb (v1).
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

export const WILLIE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/willie.glb`;
// ⭐ THE FORGE WILLIE (caP'n, 2026-09-23): "leave the willie's model that is in the Forge alone".
// willie_forge.glb is a FROZEN copy of the 2026-09-16 sculpt (sad tired eyes and all);
// the Plaza Willie gets the Blender treatment, the Forge keeps this one. Never rebuild it.
export const WILLIE_FORGE_GLB_URL = `${process.env.PUBLIC_URL || ""}/models/willie_forge.glb`;
const MODEL_TOP = 1.4;   // the 2026-09-16 sculpt's crown (willie.glb until the Blender pass lands, willie_forge.glb forever)
const NO_INK = /eyes|buckle|glyph|bookw|bodymoss|cuffs/;
const SELF_LIGHT = 0.4;
const GLYPH = 0x7ff0ff;               // the W on his pouch

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([172, 172, 172, 255, 228, 228, 228, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter;
  RAMP.needsUpdate = true;
  return RAMP;
};
let INK = null;
const inkMat = () => {
  if (INK) return INK;
  INK = new THREE.MeshBasicMaterial({ color: 0x161114, side: THREE.BackSide });
  INK.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n  transformed += normalize( normal ) * 0.0030;");
  };
  return INK;
};

function prepare(scene) {
  const root = cloneSkinned(scene);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  let glyph = null;
  meshes.forEach((o) => {
    const m = o.material;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    const vc = !!o.geometry.attributes.color;
    o.material = new THREE.MeshToonMaterial({
      color: m.color, map: m.map, gradientMap: toonRamp(), side: m.side, vertexColors: vc,
      emissive: m.color.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
    });
    // vertex-coloured parts keep their colour in the vertices: the self-light
    // has to be scaled by it too, or it washes them out to grey
    if (vc) o.material.onBeforeCompile = (sh) => {
      sh.fragmentShader = sh.fragmentShader.replace("vec3 totalEmissiveRadiance = emissive;", "vec3 totalEmissiveRadiance = emissive * vColor.rgb;");
    };
    o.frustumCulled = false;
    if (o.name === "wil_glyph") {
      glyph = o;
      o.material = new THREE.MeshBasicMaterial({ color: GLYPH, toneMapped: false });
      return;
    }
    if (NO_INK.test(o.name)) return;
    const h = o.isSkinnedMesh ? new THREE.SkinnedMesh(o.geometry, inkMat()) : new THREE.Mesh(o.geometry, inkMat());
    if (o.isSkinnedMesh) h.bind(o.skeleton, o.bindMatrix);
    h.name = o.name + "_ink"; h.frustumCulled = false;
    o.parent.add(h);
  });
  const B = {};
  root.traverse((o) => { if (o.isBone) B[o.name] = o; });
  if (B.hips) B.hips.userData.y0 = B.hips.position.y;
  return { root, B, glyph };
}

// ⚠ upperarmL / forearmL / handL at rest MUST match READ_POSE in the builder
// (build.py): the book is authored in that pose, so it only sits in his
// hand while these three numbers agree.
export function williePose(B, t, ph, amt, pt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sw = Math.sin(ph);
  for (const [k, s, off] of [["L", 1, 0], ["R", -1, Math.PI]]) {
    const lp = ph + off, fwd = Math.sin(lp), swing = Math.max(0, Math.cos(lp));
    set("thigh" + k, (-fwd * 0.38 - swing * 0.14) * amt, 0, s * 0.04);
    set("shin" + k, swing * 0.7 * amt, 0, 0);
    set("foot" + k, (-swing * 0.3 + Math.max(0, -fwd) * 0.2) * amt, s * 0.05, 0);
  }
  // the book arm: up in front of his chest, a small bob with the step
  const bob = Math.sin(ph * 2) * 0.03 * amt + Math.sin(t * 0.9) * 0.015;
  set("upperarmL", -0.42 + bob, 0.2, -0.1);
  set("forearmL", -1.05 - bob, 0.35, 0);
  set("handL", 0.15, -0.5, -0.25);
  // the free arm: swings with the walk, or lifts to point
  const fr = -Math.sin(ph) * 0.35 * amt;
  set("upperarmR", fr * (1 - pt) - 1.1 * pt, 0.1 * pt, -0.08 + 0.3 * pt);
  set("forearmR", (-0.15 + Math.min(0, fr) * 0.4) * (1 - pt) - 0.35 * pt, 0, 0);
  set("handR", -0.2 * pt, 0, 0);
  // his index has its own bone: folded into the fist unless he's pointing
  set("indexR", -2.3 * (1 - pt), 0, 0);
  if (B.indexR) B.indexR.scale.setScalar(1 - 0.45 * (1 - pt));
  set("hips", 0, sw * 0.07 * amt, Math.sin(ph) * 0.03 * amt);
  if (B.hips) B.hips.position.y = B.hips.userData.y0 + (Math.abs(Math.cos(ph)) - 0.6) * 0.022 * amt;
  set("spine", 0.03 + 0.03 * amt, -sw * 0.05 * amt, -Math.sin(ph) * 0.02 * amt);
  set("chest", -0.02 + Math.sin(t * 1.1) * 0.012, -sw * 0.04 * amt, 0);
  set("neck", 0.06, 0, 0);
  // he looks down at the book, and up at you when he points
  set("head", 0.22 * (1 - pt) - 0.05 + Math.sin(t * 0.6) * 0.02, 0.18 * (1 - pt) + Math.sin(t * 0.3) * 0.08, 0.04 * (1 - pt));
}

const POINT_EVERY = 11, POINT_FOR = 2.6;   // seconds
const smooth = (x) => { x = Math.min(1, Math.max(0, x)); return x * x * (3 - 2 * x); };

export function WillieModel({ ctl, h = 1.4, url = WILLIE_GLB_URL, top = MODEL_TOP }) {
  const gltf = useLoader(GLTFLoader, url);
  const { root, B, glyph } = useMemo(() => prepare(gltf.scene), [gltf]);
  const g = useRef(); const yaw = useRef(0); const ph = useRef(0); const amt = useRef(0); const pt = useRef(0);
  const force = typeof window !== "undefined" && /[?&]point=1/.test(window.location.search);   // review room: hold the point
  useFrame(({ clock }, dtRaw) => {
    const o = g.current; if (!o) return;
    const dt = Math.min(dtRaw || 0.016, 0.05); const t = clock.getElapsedTime() + 1.3;
    const f = (ctl && ctl.facing) || { x: 0, y: 1 };
    const want = Math.atan2(f.x || 0, f.y === undefined ? 1 : f.y);
    let d = want - yaw.current; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2;
    yaw.current += d * Math.min(1, dt * 9);
    o.rotation.y = yaw.current;
    const speed = (ctl && ctl.speed) || 0;
    amt.current += ((speed ? 1 : 0) - amt.current) * Math.min(1, dt * 6);
    ph.current += (speed ? 1 : amt.current) * dt * 7;
    // the point: a slow rise and fall inside its window, never while walking
    const c = t % POINT_EVERY;
    const want_pt = force ? 1 : smooth(c / 0.5) * smooth((POINT_FOR - c) / 0.5) * (1 - amt.current);
    pt.current += (want_pt - pt.current) * Math.min(1, dt * 8);
    williePose(B, t, ph.current, amt.current, pt.current);
    if (glyph) glyph.material.color.setHex(GLYPH).multiplyScalar(0.85 + Math.sin(t * 2.4) * 0.15);
  });
  return (
    <group ref={g}>
      <primitive object={root} scale={h / top} />
    </group>
  );
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[willie] willie.glb did not load — the painted body stands in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function WillieSculpted({ ctl, h, Fallback, url, top }) {
  const fb = Fallback ? <Fallback ctl={ctl} h={h} /> : null;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}>
        <WillieModel ctl={ctl} h={h} url={url} top={top} />
      </Suspense>
    </GlbBoundary>
  );
}

// the Forge's own Willie — the frozen 2026-09-16 body
export function WillieForgeSculpted(p) { return <WillieSculpted {...p} url={WILLIE_FORGE_GLB_URL} top={MODEL_TOP} />; }

export default WillieModel;
